import {
  ArrowRight,
  Bot,
  Boxes,
  Cloud,
  Download,
  GitMerge,
  KeyRound,
  Laptop,
  ListChecks,
  MessageSquare,
  Pause,
  Scale,
  ScanSearch,
  ShieldQuestion,
  Signpost,
  Workflow,
} from 'lucide-react'
import { m } from '#/paraglide/messages'
import { getLocale } from '#/paraglide/runtime'
import { useReleases } from '#/lib/api-releases'
import { desktopArtifact, formatBytes } from '#/lib/releases'
import { Container, MkAnchor, MkLink, Overline, SectionHead } from './mk'

export function AgentPlatformSection() {
  const choices = [
    {
      icon: Cloud,
      title: m.mk_team_bystep_title(),
      desc: m.mk_team_bystep_desc(),
      note: m.mk_team_bystep_note(),
    },
    {
      icon: KeyRound,
      title: m.mk_team_api_title(),
      desc: m.mk_team_api_desc(),
      note: m.mk_team_api_note(),
    },
    {
      icon: Laptop,
      title: m.mk_team_native_title(),
      desc: m.mk_team_native_desc(),
      note: m.mk_team_native_note(),
    },
  ]
  const controls = [
    {
      icon: Workflow,
      title: m.mk_team_control_parallel(),
      desc: m.mk_team_control_parallel_desc(),
    },
    {
      icon: MessageSquare,
      title: m.mk_team_control_message(),
      desc: m.mk_team_control_message_desc(),
    },
    {
      icon: Pause,
      title: m.mk_team_control_stop(),
      desc: m.mk_team_control_stop_desc(),
    },
  ]
  // Orkestrator E6: the opt-in controls (Reviewer, command approvals, Decision Cards), the agent registry and
  // the provider policy — each card states what it does today, no roadmap items.
  const trust = [
    { icon: ScanSearch, title: m.mk_team_reviewer_title(), desc: m.mk_team_reviewer_desc(), id: 'reviewer' },
    { icon: ShieldQuestion, title: m.mk_team_approvals_title(), desc: m.mk_team_approvals_desc(), id: 'approvals' },
    { icon: Signpost, title: m.mk_team_cards_title(), desc: m.mk_team_cards_desc(), id: 'decision-cards' },
    { icon: Boxes, title: m.mk_team_registry_title(), desc: m.mk_team_registry_desc(), id: 'registry' },
  ]
  return (
    <section
      id="agent-platform"
      className="mk-section scroll-mt-20"
      data-testid="landing-agent-platform"
    >
      <Container>
        <SectionHead
          overline={m.mk_team_overline()}
          title={m.mk_team_title()}
          lead={m.mk_team_sub()}
        />
        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          {choices.map((choice) => (
            <article key={choice.title} className="mk-path-card">
              <choice.icon className="size-6" aria-hidden />
              <h3>{choice.title}</h3>
              <p>{choice.desc}</p>
              <small className="mt-auto">{choice.note}</small>
            </article>
          ))}
        </div>
        <p className="mx-auto mt-6 max-w-4xl text-center text-sm leading-relaxed text-[var(--mk-fg-muted)]">
          {m.mk_team_planning_note()}{' '}
          <a href="/kebijakan-penyedia" className="underline underline-offset-4" data-testid="landing-policy-link">
            {m.pol_link_more()}
          </a>
        </p>
        <div className="mt-14 grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="flex flex-col items-start gap-5">
            <Overline>{m.mk_team_flow_overline()}</Overline>
            <h3 className="text-2xl font-semibold sm:text-3xl">
              {m.mk_team_flow_title()}
            </h3>
            <p className="mk-muted leading-relaxed">{m.mk_team_flow_desc()}</p>
            <ul className="flex flex-col gap-5">
              {controls.map((control) => (
                <li key={control.title} className="flex items-start gap-3">
                  <control.icon className="mt-1 size-5 shrink-0" aria-hidden />
                  <div>
                    <h4 className="font-medium">{control.title}</h4>
                    <p className="mt-1 text-sm leading-relaxed text-[var(--mk-fg-muted)]">
                      {control.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            <MkLink to="/sign-in" search={{ redirect: '/home' }} arrow>
              {m.mk_team_cta()}
            </MkLink>
          </div>
          <figure className="min-w-0 rounded-2xl border border-[var(--mk-border)] bg-[var(--mk-bg-muted)] p-5 sm:p-8">
            <figcaption className="mb-6 text-sm text-[var(--mk-fg-muted)]">
              {m.mk_team_example()}
            </figcaption>
            <ol className="flex flex-col gap-4">
              <li className="rounded-xl border border-[var(--mk-border)] bg-white p-5">
                <p className="mb-4 text-sm font-medium">
                  {m.mk_team_flow_assign()}
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[m.mk_team_example_ui(), m.mk_team_example_api()].map(
                    (task) => (
                      <div
                        key={task}
                        className="flex items-center gap-2 rounded-lg bg-[var(--mk-bg-muted)] p-3 text-sm"
                      >
                        <Bot className="size-4 shrink-0" aria-hidden />
                        <span>{task}</span>
                      </div>
                    ),
                  )}
                </div>
              </li>
              <li className="flex items-start gap-3 rounded-xl border border-[var(--mk-border)] bg-white p-5">
                <GitMerge className="mt-1 size-5 shrink-0" aria-hidden />
                <div>
                  <h4 className="font-medium">{m.mk_team_flow_verify()}</h4>
                  <p className="mt-1 text-sm leading-relaxed text-[var(--mk-fg-muted)]">
                    {m.mk_team_flow_verify_desc()}
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3 rounded-xl border border-[var(--mk-border)] bg-white p-5">
                <ListChecks className="mt-1 size-5 shrink-0" aria-hidden />
                <div>
                  <h4 className="font-medium">{m.mk_team_flow_review()}</h4>
                  <p className="mt-1 text-sm leading-relaxed text-[var(--mk-fg-muted)]">
                    {m.mk_team_flow_review_desc()}
                  </p>
                </div>
              </li>
            </ol>
          </figure>
        </div>
        <div
          className="mt-16 border-t border-[var(--mk-border)] pt-12"
          data-testid="landing-agent-trust"
        >
          <div className="flex max-w-3xl flex-col items-start gap-4">
            <Overline>{m.mk_team_trust_overline()}</Overline>
            <h3 className="text-2xl font-semibold sm:text-3xl">
              {m.mk_team_trust_title()}
            </h3>
            <p className="mk-muted leading-relaxed">{m.mk_team_trust_sub()}</p>
          </div>
          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {trust.map((item) => (
              <li
                key={item.id}
                data-testid={`landing-agent-trust-${item.id}`}
                className="flex min-w-0 flex-col gap-2 rounded-xl border border-[var(--mk-border)] bg-white p-5"
              >
                <item.icon className="size-5 shrink-0" aria-hidden />
                <h4 className="font-medium">{item.title}</h4>
                <p className="text-sm leading-relaxed text-[var(--mk-fg-muted)]">
                  {item.desc}
                </p>
              </li>
            ))}
            <li
              data-testid="landing-agent-trust-policy"
              className="flex min-w-0 flex-col gap-2 rounded-xl border border-[var(--mk-border)] bg-[var(--mk-bg-muted)] p-5"
            >
              <Scale className="size-5 shrink-0" aria-hidden />
              <h4 className="font-medium">{m.mk_team_policy_title()}</h4>
              <p className="text-sm leading-relaxed text-[var(--mk-fg-muted)]">
                {m.mk_team_policy_desc()}
              </p>
              <a
                href="/kebijakan-penyedia"
                className="mt-auto inline-flex items-center gap-2 pt-1 text-sm font-medium underline underline-offset-4"
                data-testid="landing-agent-policy-link"
              >
                {m.mk_team_policy_link()}
                <ArrowRight className="size-4 shrink-0" aria-hidden />
              </a>
            </li>
          </ul>
        </div>
      </Container>
    </section>
  )
}

export function DesktopSection() {
  // Version and size come from /downloads/releases.json (fetched after hydration); the server
  // render shows neutral copy so a stale version is never printed.
  const { data: releases } = useReleases()
  const desktop = desktopArtifact(releases)
  const steps = [
    { title: m.mk_desktop_step_1(), desc: m.mk_desktop_step_1_desc() },
    { title: m.mk_desktop_step_2(), desc: m.mk_desktop_step_2_desc() },
    { title: m.mk_desktop_step_3(), desc: m.mk_desktop_step_3_desc() },
  ]
  return (
    <section
      id="desktop"
      className="mk-section scroll-mt-20 bg-[var(--mk-bg-muted)]"
      data-testid="landing-desktop"
    >
      <Container>
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="flex min-w-0 flex-col items-start gap-5">
            <Overline>{m.mk_desktop_overline()}</Overline>
            <h2>{m.mk_desktop_title()}</h2>
            <p className="mk-lead">{m.mk_desktop_sub()}</p>
            <div className="flex flex-wrap gap-3">
              {desktop ? (
                <MkAnchor href={desktop.url} download={desktop.file} data-testid="landing-desktop-download">
                  <Download className="size-4 shrink-0" aria-hidden />
                  {m.mk_desktop_download()}
                </MkAnchor>
              ) : (
                <MkAnchor aria-disabled="true" className="pointer-events-none opacity-60" data-testid="landing-desktop-download">
                  <Download className="size-4 shrink-0" aria-hidden />
                  {m.mk_desktop_download()}
                </MkAnchor>
              )}
              <MkLink to="/serve" variant="secondary" arrow>
                {m.mk_desktop_guide()}
              </MkLink>
            </div>
            <p className="text-sm leading-relaxed text-[var(--mk-fg-muted)]" data-testid="landing-desktop-requirements">
              {desktop
                ? m.mk_desktop_requirements({ version: desktop.version, size: formatBytes(desktop.bytes, getLocale()) })
                : m.mk_desktop_requirements_pending()}{' '}
              {m.mk_desktop_needs()}
            </p>
            <p className="text-sm leading-relaxed text-[var(--mk-fg-muted)]">
              {m.mk_desktop_platforms()}
            </p>
            <a
              href="/downloads/releases.json"
              className="inline-flex items-center gap-2 text-sm underline underline-offset-4"
            >
              {m.mk_desktop_versions()}
              <ArrowRight className="size-4" aria-hidden />
            </a>
          </div>
          <div className="rounded-2xl border border-[var(--mk-border)] bg-white p-6 sm:p-8">
            <div className="mb-7 flex items-center gap-3">
              <Laptop className="size-6" aria-hidden />
              <h3>{m.mk_desktop_setup_title()}</h3>
            </div>
            <ol className="flex flex-col gap-6">
              {steps.map((step, index) => (
                <li key={step.title} className="flex items-start gap-4">
                  <span
                    className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--mk-bg-muted)] font-mono text-sm"
                    aria-hidden
                  >
                    {index + 1}
                  </span>
                  <div>
                    <h4 className="font-medium">{step.title}</h4>
                    <p className="mt-1 text-sm leading-relaxed text-[var(--mk-fg-muted)]">
                      {step.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-7 border-t border-[var(--mk-border)] pt-5 text-sm leading-relaxed text-[var(--mk-fg-muted)]">
              {m.mk_desktop_open_note()}
            </p>
          </div>
        </div>
      </Container>
    </section>
  )
}
